import React, { useCallback, useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  mdiClockOutline,
  mdiDeleteOutline,
  mdiFilterOutline,
  mdiInformationOutline,
  mdiPencilOutline,
  mdiRadioboxMarked,
} from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { AutomationService } from "../../automation/service";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import { canEditAutomation, errorMessage, useConfirm, useRunAutomation, useRunHistory } from "../../automation/useAutomations";
import { TYPE_LABELS, actionLines, conditionLines, lastRunText, runsByDay, triggerText, whenText } from "../../automation/format";
import { isColoured, lookOf, pillOf } from "../../automation/look";
import { AutomationSwitch, RunButton } from "./AutomationCard";
import { RunRow } from "./RunViews";
import { AUTOMATION_PATHS } from "./paths";
import "../../assets/styles/app-automation.css";

// One of the summary's lines (When, Only if, Then); the create flow's last
// step shows them too.
export const SummaryLine = ({ icon, label, lines }) => (
  <div className="hx-auto-line">
    <MdiIcon path={icon} size={20} />
    <div>
      <h3>{label}</h3>
      {lines.map((line, index) => (
        <p key={index}>{line}</p>
      ))}
    </div>
  </div>
);

// An automation's own page (app/screens/Automation/AutomationDetail.js): what
// it does, its switch, Run, what happened when it ran, and Edit / Delete.
function AutomationDetail() {
  const { state } = useLocation();
  const navigate = useHistory();
  const toast = useToggleTop();
  const { homeId, lookup } = useAutomationFeature();
  const { confirm, dialog } = useConfirm();
  const id = state?.id;
  const [automation, setAutomation] = useState(state?.automation ?? null);
  const [error, setError] = useState(null);
  const history = useRunHistory(id);

  const load = useCallback(async () => {
    if (!homeId || !id) return;
    try {
      setAutomation(await AutomationService.get(homeId, id));
      setError(null);
    } catch (loadError) {
      setError(errorMessage(loadError));
    }
  }, [homeId, id]);

  useEffect(() => {
    load();
  }, [load]);

  const reloadHistory = history.reload;
  const run = useRunAutomation(
    confirm,
    useCallback(() => {
      load();
      reloadHistory();
    }, [load, reloadHistory])
  );

  if (!id) return <Redirect to={AUTOMATION_PATHS.HOME} />;

  if (!automation) {
    return (
      <Main title="Automation">
        {error ? (
          <div className="hx-empty" role="alert">
            {error}
          </div>
        ) : (
          <div className="hx-skel hx-auto-skel is-tall" />
        )}
      </Main>
    );
  }

  const toggle = async (enabled) => {
    const before = automation;
    setAutomation({ ...automation, enabled });
    try {
      setAutomation(await AutomationService.setEnabled(homeId, id, enabled));
    } catch (toggleError) {
      setAutomation(before);
      toast(errorMessage(toggleError), "red");
    }
  };

  const remove = async () => {
    const yes = await confirm({
      title: "Delete automation?",
      message: `"${automation.name}" will stop running and be removed for everyone in this home.`,
      confirmLabel: "Delete",
    });
    if (!yes) return;
    try {
      await AutomationService.remove(homeId, id);
      toast("Automation deleted.", "green");
      if (navigate.length > 1) navigate.goBack();
      else navigate.push(AUTOMATION_PATHS.HOME);
    } catch (removeError) {
      toast(errorMessage(removeError), "red");
    }
  };

  const can = automation.can || {};
  const look = lookOf(automation);
  const cardColor = state?.color || look.color;
  const pill = pillOf(automation);
  const coloured = isColoured(automation);
  const conditions = conditionLines(automation, lookup);
  const actions = actionLines(automation, lookup);
  const showSwitch = can.edit && !automation.readOnly && automation.type !== "tap";
  const editable = canEditAutomation(automation);
  const deletable = can.edit && !automation.readOnly;
  // Cycling logs only steps that fail, so its state comes from the item itself.
  const cycling = automation.type === "cycling" && !automation.readOnly;
  const lastRun = lastRunText(automation.lastRun);
  const runText = lastRun || (cycling ? (automation.enabled !== false ? "Cycling on and off." : "Not cycling.") : "It hasn't run yet.");
  const next = automation.nextRunAt && automation.enabled !== false ? `${cycling ? "Next switch" : "Next"}: ${whenText(automation.nextRunAt)}` : null;

  return (
    <Main
      title={automation.name}
      headerRight={
        editable ? (
          <button
            type="button"
            className="hx-icon-btn"
            onClick={() => navigate.push(AUTOMATION_PATHS.EDIT, { automation })}
            aria-label="Edit automation"
          >
            <MdiIcon path={mdiPencilOutline} size={26} />
          </button>
        ) : null
      }
    >
      <div className="hx-auto-detail">
        <section
          className={`hx-auto-summary ${coloured ? "" : "is-off"}`}
          style={{ "--hx-auto-card": cardColor }}
          aria-label="What it does"
        >
          <div className="hx-auto-summary-top">
            <MdiIcon path={look.icon} size={18} />
            <span>{TYPE_LABELS[automation.type]}</span>
            {pill ? <span className="hx-auto-pill is-on-summary">{pill.text}</span> : null}
          </div>
          <SummaryLine icon={mdiClockOutline} label="When" lines={[triggerText(automation, lookup)]} />
          {conditions.length ? <SummaryLine icon={mdiFilterOutline} label="Only if" lines={conditions} /> : null}
          {actions.length ? <SummaryLine icon={mdiRadioboxMarked} label="Then" lines={actions} /> : null}
        </section>

        {automation.status === "paused" || automation.status === "review" || automation.readOnly ? (
          <p className="hx-auto-warning">
            <MdiIcon path={mdiInformationOutline} size={18} />
            {automation.readOnly
              ? "This runs on the device itself. Change it in the device's settings."
              : automation.statusReason ||
                (automation.status === "review"
                  ? "This was moved from the old screens. Check it, then switch it on."
                  : "This is paused. An owner can save it again to start it.")}
          </p>
        ) : null}

        <section className="hx-auto-box" aria-label="Run and switch">
          {showSwitch ? (
            <div className="hx-auto-box-row">
              <span className="hx-auto-box-label">{automation.enabled !== false ? "On" : "Off"}</span>
              <AutomationSwitch automation={automation} onToggle={toggle} />
            </div>
          ) : null}
          {can.run ? (
            <div className="hx-auto-box-row">
              <span className="hx-auto-box-label">Run it now</span>
              <RunButton onRun={() => run(automation)} label={`Run ${automation.name} now`} />
            </div>
          ) : null}
          <p className="hx-auto-box-text">
            {runText}
            {next ? (
              <>
                <br />
                {next}
              </>
            ) : null}
          </p>
          {automation.notify && automation.notify !== "off" ? (
            <p className="hx-auto-box-text">
              {automation.notify === "always" ? "Notifies every run" : "Notifies when a run fails"}
              {automation.notifyTo ? ` · to ${automation.notifyTo}` : ""}
            </p>
          ) : null}
          {automation.createdBy ? (
            <p className="hx-auto-box-text">
              Made by {automation.createdBy}
              {automation.updatedBy && automation.updatedBy !== automation.createdBy ? ` · last changed by ${automation.updatedBy}` : ""}
            </p>
          ) : null}
        </section>

        <h2 className="hx-auto-section">History</h2>
        {/* A heading per day, as in Recent activity: the rows only give the time. */}
        {history.runs.length ? (
          runsByDay(history.runs).map((day) => (
            <section key={day.title} aria-label={day.title}>
              <h3 className="hx-auto-day">{day.title}</h3>
              <ul className="hx-auto-runs">
                {day.runs.map((item) => (
                  <RunRow key={item.id} run={item} />
                ))}
              </ul>
            </section>
          ))
        ) : (
          <p className="hx-auto-box-text">{history.loading ? "Loading…" : history.error || "Nothing yet."}</p>
        )}
        {history.next ? (
          <button type="button" className="hx-auto-more" onClick={history.loadMore} disabled={history.loading}>
            {history.loading ? "Loading…" : "Show more"}
          </button>
        ) : null}

        {deletable ? (
          <button type="button" className="hx-auto-delete" onClick={remove}>
            <MdiIcon path={mdiDeleteOutline} size={20} />
            Delete automation
          </button>
        ) : null}
      </div>
      {dialog}
    </Main>
  );
}

export default AutomationDetail;
